// src/App.jsx
import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import AppShell from './components/layout/AppShell';
import LoginPage from './pages/LoginPage';
import ChatPage from './pages/ChatPage';
import VINPage from './pages/VINPage';
import FleetPage from './pages/FleetPage';
import InspectionPage from './pages/InspectionPage';
import DiagnosticsPage from './pages/DiagnosticsPage';
import HistoryPage from './pages/HistoryPage';
import AnalyticsPage from './pages/AnalyticsPage';
import ReportsPage from './pages/ReportsPage';
import PartsPage from './pages/PartsPage';
import RepairAdvicePage from './pages/RepairAdvicePage';
import CarScannerPage from './pages/CarScannerPage';
import GaragesPage from './pages/GaragesPage';
import WorkflowAutomationPage from './pages/WorkflowAutomationPage';
import ConsoleShell from './components/layout/ConsoleShell';
import ProfileSettingsPage from './pages/ProfileSettingsPage';
import ApiKeysPage from './pages/ApiKeysPage';
import ModelsPage from './pages/ModelsPage';
import BillingPage from './pages/BillingPage';
import DocumentationPage from './pages/DocumentationPage';
import ConsoleDashboard from './pages/ConsoleDashboard';
import LandingPage from './pages/LandingPage';

// Admin Suite
import AdminShell from './components/layout/AdminShell';
import RequireAdmin from './components/auth/RequireAdmin';
import AdminDashboardPage from './pages/admin/AdminDashboardPage';
import AdminUsersPage from './pages/admin/AdminUsersPage';
import AdminGaragesPage from './pages/admin/AdminGaragesPage';
import AdminBookingsPage from './pages/admin/AdminBookingsPage';
import AdminPlansPage from './pages/admin/AdminPlansPage';
import AdminDiscountsPage from './pages/admin/AdminDiscountsPage';
import AdminSystemPage from './pages/admin/AdminSystemPage';

import UpgradeModal from './components/layout/UpgradeModal';
import './index.css';

function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh', color:'#607D8B' }}>Loading AAIA…</div>;
  return user ? children : <Navigate to="/login" replace />;
}

function GlobalRedirectHandler() {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const reference = params.get('reference');
    const vin = params.get('vin');

    if (reference) {
      if (vin && !location.pathname.includes('/history')) {
        navigate(`/history?vin=${vin}&paid=true&reference=${reference}`, { replace: true });
      } else if (!vin && !location.pathname.includes('/billing') && !location.pathname.includes('/history')) {
        navigate(`/console/billing?reference=${reference}`, { replace: true });
      }
    }
  }, [location, navigate]);

  return null;
}

function AppContainer() {
  const [showUpgrade, setShowUpgrade] = useState(false);

  useEffect(() => {
    const handler = () => setShowUpgrade(true);
    window.addEventListener('creditsExhausted', handler);
    return () => window.removeEventListener('creditsExhausted', handler);
  }, []);

  return (
    <>
      <BrowserRouter>
        <GlobalRedirectHandler />
        {showUpgrade && <UpgradeModal onClose={() => setShowUpgrade(false)} />}
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route element={<RequireAuth><AppShell /></RequireAuth>}>
            <Route path="/chat"           element={<ChatPage />} />
            <Route path="/chat/:id"       element={<ChatPage />} />
            <Route path="/car-scanner"    element={<CarScannerPage />} />
            <Route path="/vin"            element={<VINPage />} />
            <Route path="/fleet"          element={<Navigate to="/chat" replace />} />
            <Route path="/inspection"     element={<InspectionPage />} />
            <Route path="/inspection/:id" element={<InspectionPage />} />
            <Route path="/diagnostics"    element={<DiagnosticsPage />} />
            <Route path="/history"        element={<HistoryPage />} />
            <Route path="/analytics"      element={<AnalyticsPage />} />
            <Route path="/reports"        element={<ReportsPage />} />
            <Route path="/repair-advice"  element={<RepairAdvicePage />} />
            <Route path="/parts"          element={<Navigate to="/repair-advice" replace />} />
            <Route path="/garages"        element={<GaragesPage />} />
            <Route path="/workflow"       element={<WorkflowAutomationPage />} />
          </Route>
          
          {/* Developer / Enterprise Console */}
          <Route path="/console" element={<RequireAuth><ConsoleShell /></RequireAuth>}>
            <Route index element={<Navigate to="/console/dashboard" replace />} />
            <Route path="dashboard" element={<ConsoleDashboard />} />
            <Route path="profile" element={<ProfileSettingsPage />} />
            <Route path="api-keys" element={<ApiKeysPage />} />
            <Route path="models" element={<ModelsPage />} />
            <Route path="billing" element={<BillingPage />} />
            <Route path="docs" element={<DocumentationPage />} />
            <Route path="*" element={<Navigate to="/console/dashboard" replace />} />
          </Route>

          {/* Independent Admin Management Suite */}
          <Route path="/admin" element={<RequireAdmin><AdminShell /></RequireAdmin>}>
            <Route index element={<Navigate to="/admin/dashboard" replace />} />
            <Route path="dashboard" element={<AdminDashboardPage />} />
            <Route path="users" element={<AdminUsersPage />} />
            <Route path="garages" element={<AdminGaragesPage />} />
            <Route path="bookings" element={<AdminBookingsPage />} />
            <Route path="plans" element={<AdminPlansPage />} />
            <Route path="discounts" element={<AdminDiscountsPage />} />
            <Route path="system" element={<AdminSystemPage />} />
            <Route path="*" element={<Navigate to="/admin/dashboard" replace />} />
          </Route>

          <Route path="*" element={<Navigate to="/chat" replace />} />
        </Routes>
      </BrowserRouter>
    </>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AppContainer />
    </AuthProvider>
  );
}
